import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Long-form speech capture for AI Dictate (PD-1516), on the same browser Web
 * Speech engine as `VoiceInputButton` — but buffering into its OWN state
 * instead of the bound field: nothing may touch the note before the provider
 * accepts the AI-formatted preview. Adds what dictation needs and the shared
 * button lacks: continuous restart on the engine's silence auto-stop, and
 * Pause/Resume.
 */

type SpeechRecognitionLike = {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  onresult: ((event: SpeechResultEventLike) => void) | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort?: () => void
}

type SpeechResultEventLike = {
  resultIndex: number
  results: {
    length: number
    [index: number]: { isFinal: boolean; [index: number]: { transcript: string } }
  }
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getSpeechRecognition(): SpeechRecognitionCtor | undefined {
  const w = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition
}

export type SpeechCaptureStatus = 'idle' | 'recording' | 'paused'

type Session = {
  recognition: SpeechRecognitionLike
  /** Finalized utterances so far; interim text rides separately. */
  finals: string[]
  interim: string
  active: boolean
  paused: boolean
  /** Engine silence auto-stops survived with nothing captured yet. */
  silentRestarts: number
  stopResolvers: (() => void)[]
}

export function useSpeechCapture(): {
  supported: boolean
  status: SpeechCaptureStatus
  /** Finals + interim, for the live readout under the recording bar. */
  liveText: string
  error: string | null
  start: () => void
  pause: () => void
  resume: () => void
  /** Stop and return the final transcript (finals + trailing interim). */
  finish: () => Promise<string>
  cancel: () => void
} {
  const [status, setStatus] = useState<SpeechCaptureStatus>('idle')
  const [liveText, setLiveText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const sessionRef = useRef<Session | null>(null)
  const supported = typeof window !== 'undefined' && Boolean(getSpeechRecognition())

  const teardown = useCallback((session: Session) => {
    session.active = false
    session.recognition.onresult = null
    session.recognition.onerror = null
    session.recognition.onend = null
    try {
      session.recognition.abort?.()
      session.recognition.stop()
    } catch {
      // Already stopped.
    }
    session.stopResolvers.forEach((resolve) => resolve())
    session.stopResolvers = []
  }, [])

  const start = useCallback(() => {
    const Ctor = getSpeechRecognition()
    if (!Ctor || sessionRef.current?.active) return
    setError(null)
    setLiveText('')

    const recognition = new Ctor()
    recognition.lang = 'en-US'
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    const session: Session = {
      recognition,
      finals: [],
      interim: '',
      active: true,
      paused: false,
      silentRestarts: 0,
      stopResolvers: [],
    }
    sessionRef.current = session

    recognition.onresult = (event) => {
      if (sessionRef.current !== session) return
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        const transcript = result[0]?.transcript.trim() ?? ''
        if (!transcript) continue
        if (result.isFinal) session.finals.push(transcript)
        else interim = [interim, transcript].filter(Boolean).join(' ')
      }
      session.interim = interim
      if (session.finals.length || session.interim) {
        session.silentRestarts = 0
        setError((current) => (current === 'nothing-heard' ? null : current))
      }
      setLiveText([...session.finals, session.interim].filter(Boolean).join(' '))
    }
    recognition.onerror = (event) => {
      if (sessionRef.current !== session) return
      // 'no-speech'/'aborted' are routine engine hiccups the restart absorbs.
      if (event.error === 'no-speech' || event.error === 'aborted') return
      setError(event.error)
      // Any other error halts the engine the way Pause does: a denied mic
      // re-raises on every restart (PD-1582), so `onend` must not restart and
      // the silent-restart counter must not overwrite the real cause. Captured
      // text stays for Finish; Resume retries once. Stop explicitly too — a
      // Finish before the engine's own `end` must not leave it capturing.
      session.paused = true
      setStatus('paused')
      try {
        recognition.stop()
      } catch {
        // Already ending.
      }
    }
    recognition.onend = () => {
      if (sessionRef.current !== session) return
      // Interim text dies with the engine session — promote it so a silence
      // auto-stop can't drop trailing words.
      if (session.interim) {
        session.finals.push(session.interim)
        session.interim = ''
      }
      if (session.active && !session.paused) {
        // The engine auto-stops on silence and we restart it; several silent
        // cycles with nothing captured means the mic is muted or blocked at
        // the OS level — surface it instead of looking alive while deaf.
        if (session.finals.length === 0 && !session.interim) {
          session.silentRestarts += 1
          if (session.silentRestarts >= 3) setError('nothing-heard')
        }
        try {
          recognition.start()
          return
        } catch {
          // The engine refused the restart. Halt like the error path: the bar
          // offers Resume instead of claiming to record, Finish keeps the text.
          setError('microphone-unavailable')
          session.paused = true
          setStatus('paused')
        }
      }
      session.stopResolvers.forEach((resolve) => resolve())
      session.stopResolvers = []
    }

    try {
      recognition.start()
      setStatus('recording')
    } catch {
      // The engine refused the first start. Keep the session and halt like the
      // error path: the bar shows the cause with Resume as the retry, never a
      // live microphone over no engine.
      setError('microphone-unavailable')
      session.paused = true
      setStatus('paused')
    }
  }, [])

  const pause = useCallback(() => {
    const session = sessionRef.current
    if (!session?.active || session.paused) return
    session.paused = true
    setStatus('paused')
    try {
      session.recognition.stop()
    } catch {
      // Already stopped.
    }
  }, [])

  const resume = useCallback(() => {
    const session = sessionRef.current
    if (!session?.active || !session.paused) return
    session.paused = false
    setStatus('recording')
    try {
      session.recognition.start()
      // The retry took: the halt's cause is stale once the engine records again.
      setError(null)
    } catch {
      // The engine refused the start (still ending). Stay paused rather than
      // claim to record over a stopped engine — the provider resumes again.
      session.paused = true
      setStatus('paused')
    }
  }, [])

  const finish = useCallback(async (): Promise<string> => {
    const session = sessionRef.current
    if (!session) return ''
    session.active = false
    if (!session.paused) {
      await new Promise<void>((resolve) => {
        session.stopResolvers.push(resolve)
        try {
          session.recognition.stop()
        } catch {
          resolve()
        }
      })
    }
    const text = [...session.finals, session.interim].filter(Boolean).join(' ').trim()
    // A paused session skipped the stop-and-wait above, so its engine and
    // handlers would outlive the reference we drop next and could still be
    // holding the microphone when the next dictation starts.
    if (session.paused) teardown(session)
    sessionRef.current = null
    setStatus('idle')
    setLiveText('')
    return text
  }, [teardown])

  const cancel = useCallback(() => {
    const session = sessionRef.current
    if (session) teardown(session)
    sessionRef.current = null
    setStatus('idle')
    setLiveText('')
  }, [teardown])

  useEffect(() => {
    return () => {
      if (sessionRef.current) teardown(sessionRef.current)
    }
  }, [teardown])

  return { supported, status, liveText, error, start, pause, resume, finish, cancel }
}
